import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Search, ArrowRight, Tag, BookOpen } from 'lucide-react';
import { newsService } from '../services/newsService';
import type { NewsArticle } from '../types/news';
import { LazySection } from '../components/ui/LazySection';

export function News() {
  const [news, setNews] = useState<NewsArticle[]>(() => newsService.getInitialNews());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Todas');

  useEffect(() => {
    let isMounted = true;
    newsService.getAllNews()
      .then((data) => {
        if (isMounted) {
          setNews(data);
        }
      })
      .catch((err) => {
        console.warn('Error loading news:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Extract unique categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    set.add('Todas');
    news.forEach((item) => {
      if (item.category) set.add(item.category);
    });
    return Array.from(set);
  }, [news]);

  // Filtered news
  const filteredNews = useMemo(() => {
    return news.filter((item) => {
      const matchesCategory =
        selectedCategory === 'Todas' || item.category === selectedCategory;
      const matchesSearch =
        searchTerm.trim() === '' ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.some((p) => p.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [news, selectedCategory, searchTerm]);

  // Featured article is the first featured article or the first in list
  const featuredArticle = useMemo(() => {
    return filteredNews.find((item) => item.featured) || filteredNews[0];
  }, [filteredNews]);

  // Other articles excluding the featured one (if shown on top)
  const remainingNews = useMemo(() => {
    if (!featuredArticle) return [];
    return filteredNews.filter((item) => item.id !== featuredArticle.id);
  }, [filteredNews, featuredArticle]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-300 pb-24">
      {/* Hero Section */}
      <section className="relative w-full h-[40vh] md:h-[48vh] pt-20 flex items-center justify-center overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img
            src="/assets/hero/ordenaciones.webp"
            alt="Noticias del Seminario Redemptoris Mater"
            className="w-full h-full object-cover object-center opacity-30 blur-[2px] scale-105 transform"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/70 to-zinc-950" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs tracking-widest uppercase mb-4 backdrop-blur-sm">
            <BookOpen size={14} className="text-zinc-300" />
            <span>Actualidad y Vida Misionera</span>
          </div>

          <h1 className="font-neocat text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-widest mb-4 bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 bg-clip-text text-transparent drop-shadow-lg uppercase">
            NOTICIAS
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 mx-auto rounded-full mb-4 opacity-80" />
          <p className="font-neocat text-sm md:text-base text-zinc-400 max-w-xl mx-auto font-light leading-relaxed">
            Acontecimientos, ordenaciones sacerdotales y momentos de gracia en nuestra casa de formación.
          </p>
        </div>
      </section>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 relative z-20">
        {/* Controls: Search and Categories */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pb-8 border-b border-white/10">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-xs font-neocat tracking-wider uppercase whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-zinc-100 text-zinc-950 font-semibold shadow-md'
                    : 'bg-zinc-900/60 text-zinc-400 hover:text-white hover:bg-zinc-800 border border-white/5'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-72">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Buscar noticia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900/80 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all font-neocat"
            />
          </div>
        </div>

        {/* Empty state */}
        {filteredNews.length === 0 && (
          <div className="py-24 text-center max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-900 flex items-center justify-center border border-white/10 text-zinc-500">
              <Search size={28} />
            </div>
            <h3 className="font-neocat text-xl text-zinc-200 mb-2 font-semibold">
              No se encontraron noticias
            </h3>
            <p className="font-neocat text-zinc-400 text-sm mb-6">
              No hay publicaciones que coincidan con los criterios de búsqueda o categoría seleccionada.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('Todas');
              }}
              className="px-5 py-2 rounded-full text-xs font-neocat uppercase tracking-wider bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors"
            >
              Restablecer filtros
            </button>
          </div>
        )}

        {/* Featured Article Card */}
        {featuredArticle && (
          <div className="my-10">
            <LazySection>
              <article className="group relative bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:border-white/30 transition-all duration-500 shadow-2xl">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
                  {/* Image Column */}
                  <div className="lg:col-span-7 relative overflow-hidden min-h-[300px] sm:min-h-[380px] lg:min-h-[440px]">
                    <img
                      src={featuredArticle.coverImage}
                      alt={featuredArticle.coverImageAlt || featuredArticle.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent lg:hidden" />
                    
                    {/* Badge */}
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="px-3 py-1 rounded-full text-[11px] font-neocat tracking-widest uppercase bg-gradient-to-r from-zinc-300 via-zinc-100 to-zinc-400 text-zinc-950 font-bold backdrop-blur-sm shadow-md shadow-white/5">
                        Destacada
                      </span>
                      {featuredArticle.category && (
                        <span className="px-3 py-1 rounded-full text-[11px] font-neocat tracking-widest uppercase bg-zinc-900/80 text-zinc-200 border border-white/10 backdrop-blur-sm">
                          {featuredArticle.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Text Details Column */}
                  <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
                    <div>
                      {/* Date & Author */}
                      <div className="flex items-center gap-3 text-xs text-zinc-400 font-neocat mb-4">
                        <span className="flex items-center gap-1.5">
                          <Calendar size={14} className="text-zinc-400" />
                          {featuredArticle.displayDate}
                        </span>
                        <span>•</span>
                        <span className="text-zinc-500">
                          {featuredArticle.gallery?.length ? `${featuredArticle.gallery.length} fotos` : 'Artículo'}
                        </span>
                      </div>

                      {/* Title */}
                      <h2 className="font-neocat text-2xl sm:text-3xl lg:text-4xl font-bold tracking-wide text-zinc-100 group-hover:text-white transition-colors duration-300 leading-tight mb-4">
                        <Link to={`/noticias/${featuredArticle.slug}`}>
                          {featuredArticle.title}
                        </Link>
                      </h2>

                      {/* Excerpt */}
                      <p className="font-neocat text-zinc-300/90 text-sm sm:text-base leading-relaxed font-light mb-6 line-clamp-4">
                        {featuredArticle.summary}
                      </p>
                    </div>

                    {/* CTA Button */}
                    <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                      <Link
                        to={`/noticias/${featuredArticle.slug}`}
                        className="inline-flex items-center gap-2 text-sm font-neocat tracking-widest uppercase text-zinc-200 group-hover:text-white transition-colors"
                      >
                        <span>Leer Noticia Completa</span>
                        <ArrowRight size={16} className="transform group-hover:translate-x-1.5 transition-transform text-zinc-400 group-hover:text-white" />
                      </Link>

                      {featuredArticle.gallery && (
                        <span className="text-xs font-neocat text-zinc-500 flex items-center gap-1">
                          <Tag size={12} />
                          Galería incluida
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            </LazySection>
          </div>
        )}

        {/* Other News Grid */}
        {remainingNews.length > 0 && (
          <div className="mt-16">
            <h3 className="font-neocat text-xl md:text-2xl font-bold tracking-widest text-zinc-200 mb-8 uppercase flex items-center gap-3">
              <span>Otras Publicaciones</span>
              <div className="flex-1 h-px bg-white/10" />
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {remainingNews.map((article) => (
                <LazySection key={article.id}>
                  <article className="group flex flex-col h-full bg-zinc-900/40 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 hover:bg-zinc-900/70 transition-all duration-300 shadow-xl flex-1">
                    {/* Cover image */}
                    <Link to={`/noticias/${article.slug}`} className="relative h-56 overflow-hidden block">
                      <img
                        src={article.coverImage}
                        alt={article.coverImageAlt || article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-60" />
                      {article.category && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-neocat tracking-widest uppercase bg-zinc-900/90 text-zinc-200 border border-white/10 backdrop-blur-sm">
                            {article.category}
                          </span>
                        </div>
                      )}
                    </Link>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-1 justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs text-zinc-400 font-neocat mb-3">
                          <Calendar size={13} className="text-zinc-400" />
                          <span>{article.displayDate}</span>
                        </div>

                        <h4 className="font-neocat text-xl font-bold text-zinc-100 group-hover:text-white transition-colors duration-300 mb-3 leading-snug">
                          <Link to={`/noticias/${article.slug}`}>
                            {article.title}
                          </Link>
                        </h4>

                        <p className="font-neocat text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-6 font-light">
                          {article.summary}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-white/5 flex items-center justify-between">
                        <Link
                          to={`/noticias/${article.slug}`}
                          className="text-xs font-neocat tracking-widest uppercase text-zinc-300 group-hover:text-white flex items-center gap-1.5 transition-colors"
                        >
                          <span>Leer más</span>
                          <ArrowRight size={14} className="transform group-hover:translate-x-1 transition-transform" />
                        </Link>

                        {article.gallery?.length ? (
                          <span className="text-[11px] font-neocat text-zinc-500">
                            {article.gallery.length} fotos
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </article>
                </LazySection>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default News;
